import {useState, useEffect} from 'react';
import {observer} from 'mobx-react-lite';
import {Form, Formik, FormikProps, FormikHelpers} from 'formik';
import {useNavigate} from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {getOrganizationTypeOptions} from '../../../entity/enums/organization-type';
import {RegistrationOption} from '../../../entity/enums/registration-option';
import {UserOnboardStep} from '../../../entity/enums/user-onboard-step';
import {InvitationCodeDto} from '../../../entity/dto/invitation-code-dto';
import {InvitationStep} from '../../../entity/enums/invitation-step';
import {UserOnboardDto, UserDetailsDto, OrganizationDetailsDto} from '../../../entity/dto/user-onboard-dto';
import {DropdownOption} from '../../../entity/core/dropdown-option';
import {State} from '../../../entity/model/state';
import {useService} from '../../../infrastructure/service/service';
import LoadingComponent from '../components/widget/LoadingComponent';
import {User} from '../../../entity/model/user';

import '../../../assets/css/style.css';
import '../../../assets/css/pages/self-registration.css';

const SelfRegistration = (): JSX.Element => {
  const {stateService, userService} = useService();
  const navigate = useNavigate();
  
  const [selectedOption, setSelectedOption] = useState<RegistrationOption>(RegistrationOption.NONE);
  const [stateOptions, setStateOptions] = useState<DropdownOption[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<UserOnboardStep>(UserOnboardStep.USER_DETAILS);
  const [userOnboardDto, setUserOnboardDto] = useState<UserOnboardDto>();
  const [currentUser, setCurrentUser] = useState<User>();
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [invitationStep, setInvitationStep] = useState<InvitationStep>(InvitationStep.USER_DETAILS);
  const [userDetails, setUserDetails] = useState<UserDetailsDto>();
  const [userDetailsCompleted, setUserDetailsCompleted] = useState<boolean>(false);

  useEffect(() => {
    const init = async (): Promise<void> => {
      setLoading(true);
      
      const states = await stateService.getStates();
      setStateOptions(states.map((state: State) => ({
        key: state.shortName,
        value: state.shortName,
        text: state.shortName
      })));

      const user = await userService.getCurrentUser();
      setCurrentUser(user);

      if(user && user.organizationID) {
        navigate('/grant-ai');
        return;
      }

      setLoading(false);
    };
    
    init();
  }, []);

  const handleOptionSelect = (option: RegistrationOption): void => {
    setSelectedOption(option);
    if (option === RegistrationOption.EXISTING)
      setInvitationStep(InvitationStep.INVITATION_CODE);
    else if (option === RegistrationOption.NEW)
      setCurrentStep(UserOnboardStep.ORGANIZATION_DETAILS);
  }

  const handleBackToOptions = (): void => {
    setSelectedOption(RegistrationOption.NONE);
    setCurrentStep(UserOnboardStep.USER_DETAILS);
    setInvitationStep(InvitationStep.INVITATION_CODE);
    setUserDetails(undefined);
  }

  const handleUserDetailsNext = (values: UserDetailsDto): void => {
    setUserOnboardDto(prev => ({
      firstName: values.firstName,
      lastName: values.lastName,
      organization: prev?.organization || userService.getOrganizationDetailsDto()
    }));

    setUserDetailsCompleted(true);
  }

  const handleInvitationCodeSubmit = async (values: InvitationCodeDto, { setFieldError }: FormikHelpers<InvitationCodeDto>): Promise<void> => {
    setSubmitLoading(true);
    
    const success = await userService.acceptInvitation(values.invitationCode, userOnboardDto?.firstName || '', userOnboardDto?.lastName || '');
    if (success)
      setTimeout(() => window.location.reload(), 1000);
    else {
      setFieldError('invitationCode', 'Invalid invitation code. Please re-enter the correct code and try again.');
      setSubmitLoading(false);
    }
  }

  const handleOrganizationSubmit = async (values: OrganizationDetailsDto): Promise<void> => {
    setSubmitLoading(true);
    
    const updatedUserOnboardDto: UserOnboardDto = {
      ...userOnboardDto!,
      organization: values
    };

    const success = await userService.onboardUser(currentUser?.id ?? '', updatedUserOnboardDto);
    if (success) {
      setTimeout(() => window.location.reload(), 1000);
    } else
      setSubmitLoading(false);
  }

  const validateUserDetails = (values: UserDetailsDto): Record<string, string> => {
    const errors: Record<string, string> = {};

    if (!values.firstName) 
      errors.firstName = 'First name is required';
    else if (!values.firstName.trim()) 
      errors.firstName = 'First name cannot be empty';

    if (!values.lastName) 
      errors.lastName = 'Last name is required';
    else if (!values.lastName.trim()) 
      errors.lastName = 'Last name cannot be empty';

    return errors;
  }

  const validateInvitationCode = (values: InvitationCodeDto): Record<string, string> => {
    const errors: Record<string, string> = {};

    if (!values.invitationCode)
      errors.invitationCode = 'Invitation code is required';
    else if (!values.invitationCode.trim())
      errors.invitationCode = 'Invitation code cannot be empty';

    return errors;
  }

  const validateOrganizationDto = (values: OrganizationDetailsDto): Record<string, any> => {
    const errors: Record<string, any> = {};

    if (!values.name) 
      errors.name = 'Organization name is required';
    else if (!values.name.trim()) 
      errors.name = 'Organization name cannot be empty';

    if (!values.type) 
      errors.type = 'Organization type is required';

    if (!values.address?.line1) 
      errors.address = {...(errors.address || {}), line1: 'Address line 1 is required'};
    else if (!values.address.line1.trim()) 
      errors.address = {...(errors.address || {}), line1: 'Address line 1 cannot be empty'};

    if (!values.address?.city) 
      errors.address = {...(errors.address || {}), city: 'City is required'};
    else if (!values.address.city.trim()) 
      errors.address = {...(errors.address || {}), city: 'City cannot be empty'};

    if (!values.address?.state) 
      errors.address = {...(errors.address || {}), state: 'State is required'};
    else if (!values.address.state.trim()) 
      errors.address = {...(errors.address || {}), state: 'State cannot be empty'};

    if (!values.address?.zip) 
      errors.address = {...(errors.address || {}), zip: 'ZIP code is required'};
    else if (!values.address.zip.trim()) 
      errors.address = {...(errors.address || {}), zip: 'ZIP code cannot be empty'};
    else if (!/^\d+$/.test(values.address.zip)) 
      errors.address = {...(errors.address || {}), zip: 'ZIP code must contain only numbers'};

    if (!values.phoneNumber) 
      errors.phoneNumber = 'Phone number is required';
    else if (!values.phoneNumber.trim()) 
      errors.phoneNumber = 'Phone number cannot be empty';
    else if (!/^\+?[\d\s]+$/.test(values.phoneNumber)) 
      errors.phoneNumber = 'Phone number must contain only numbers';
    else if (!/\d/.test(values.phoneNumber)) 
      errors.phoneNumber = 'Phone number must contain at least one digit';

    return errors;
  }

  const renderOptionSelection = (): JSX.Element => {
    return (
      <Box className={'self-registration-container'}>
        <Box className={'self-registration-header'}>
          <Typography variant='h4' className={'self-registration-title'}>
            Complete Company Profile
          </Typography>
          <Typography variant='body1' className={'self-registration-subtitle'}>
            Select your preferred option to continue
          </Typography>
        </Box>
        <Box className={'self-registration-options'}>
          <Box className={'option-cards-container'}>
            <Card 
              className={'option-card'}
              onClick={(): void => handleOptionSelect(RegistrationOption.EXISTING)}>
              <CardContent className={'option-card-content'}>
                <Typography variant='h6' className={'option-title'}>Join an Existing Organization</Typography>
                <Typography variant='body2' className={'option-description'}>
                  Join an existing organization using an invitation code
                </Typography>
              </CardContent>
            </Card>
            <Card 
              className={'option-card'}
              onClick={(): void => handleOptionSelect(RegistrationOption.NEW)}>
              <CardContent className={'option-card-content'}>
                <Typography variant='h6' className={'option-title'}>Create New Organization</Typography>
                <Typography variant='body2' className={'option-description'}>
                  Set up a new organization with your details and get started
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>
        <Box className={'form-actions-left'}>
          <Button
            id={'btn-back-to-user-details'}
            disableTouchRipple
            onClick={(): void => {
              setUserDetailsCompleted(false);
              setSelectedOption(RegistrationOption.NONE);}}
            className={'zitles-button cancel'}>
            Back
          </Button>
        </Box>
      </Box>
    );
  }

  const renderInvitationCodeForm = (): JSX.Element => {
    return (
      <Box className={'self-registration-container'}>
        <Box className={'self-registration-header'}>
          <Typography variant='h4' className={'self-registration-title'}>
            Enter Invitation Code
          </Typography>
          <Typography variant='body1' className={'self-registration-subtitle'}>
            Enter the invitation code provided by your organization
          </Typography>
        </Box>
        <Paper className={'form-container'} elevation={0}>
          <Formik
            key="invitation-code-form"
            initialValues={{invitationCode: ''} as InvitationCodeDto}
            validate={validateInvitationCode}
            onSubmit={handleInvitationCodeSubmit}
            enableReinitialize={true}>
            {({values, errors, touched, handleChange, handleBlur, handleSubmit}: FormikProps<InvitationCodeDto>): JSX.Element => (
              <Form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid size={12}>
                    <TextField
                      id={'input-invitation-code'}
                      required
                      fullWidth
                      label='Invitation Code'
                      name='invitationCode'
                      value={values.invitationCode}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.invitationCode && Boolean(errors.invitationCode)}
                      helperText={touched.invitationCode && errors.invitationCode}
                      placeholder='Enter your invitation code here'
                      className={'zitles-textfield'}/>
                  </Grid>
                </Grid>
                <Box className={'form-actions'}>
                  <Button
                    id={'btn-back-to-user-details'}
                    disableTouchRipple
                    onClick={handleBackToOptions}
                    className={'zitles-button cancel'}>
                    Back
                  </Button>
                  <Button
                    id={'btn-submit-invitation'}
                    type='submit'
                    variant='contained'
                    disabled={submitLoading}
                    className={`zitles-button ${submitLoading ? 'disable' : ''}`}>
                    {
                      submitLoading ? 'Accepting Invitation...' : 'Accept Invitation'
                    }
                  </Button>
                </Box>
              </Form>
            )}
          </Formik>
        </Paper>
      </Box>
    );
  }

  const renderUserDetailsForm = (): JSX.Element => {
    return (
      <Box className={'self-registration-container'}>
        <Box className={'self-registration-header'}>
          <Typography variant='h4' className={'self-registration-title'}>User Details</Typography>
          <Typography variant='body1' className={'self-registration-subtitle'}>
            Enter your personal information to get started
          </Typography>
        </Box>
        <Paper className={'form-container'} elevation={0}>
          <Formik
            initialValues={userService.getUserDetailsDto(userOnboardDto)}
            validate={validateUserDetails}
            onSubmit={handleUserDetailsNext}
            enableReinitialize={true}>
            {({values, errors, touched, handleChange, handleBlur, handleSubmit}: FormikProps<UserDetailsDto>): JSX.Element => (
              <Form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid size={6}>
                    <TextField
                      id={'input-first-name'}
                      required
                      fullWidth
                      label='First Name'
                      name='firstName'
                      value={values.firstName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.firstName && Boolean(errors.firstName)}
                      helperText={touched.firstName && errors.firstName}
                      className={'zitles-textfield'}/>
                  </Grid>
                  <Grid size={6}>
                    <TextField
                      id={'input-last-name'}
                      required
                      fullWidth
                      label='Last Name'
                      name='lastName'
                      value={values.lastName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.lastName && Boolean(errors.lastName)}
                      helperText={touched.lastName && errors.lastName}
                      className={'zitles-textfield'}/>
                  </Grid>
                </Grid>

                <Box className={'form-actions-right'}>
                  <Button
                    id={'btn-next-to-organization'}
                    type='submit'
                    variant='contained'
                    className={'zitles-button'}>
                    Next
                  </Button>
                </Box>
              </Form>
            )}
          </Formik>
        </Paper>
      </Box>
    );
  }

  const renderOrganizationForm = (): JSX.Element => {
    return (
      <Box className={'self-registration-container'}>
        <Box className={'self-registration-header'}>
          <Typography variant='h4' className={'self-registration-title'}>
            Create New Organization
          </Typography>
          <Typography variant='body1' className={'self-registration-subtitle'}>
            Enter your organization details to get started
          </Typography>
        </Box>
        <Paper className={'form-container'} elevation={0}>
          <Formik
            initialValues={userService.getOrganizationDetailsDto(userOnboardDto)}
            validate={validateOrganizationDto}
            onSubmit={handleOrganizationSubmit}
            enableReinitialize={true}>
            {({values, errors, touched, handleChange, handleBlur, handleSubmit}: FormikProps<OrganizationDetailsDto>): JSX.Element => (
              <Form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid size={12}>
                    <TextField
                      id={'input-organization-name'}
                      required
                      fullWidth
                      label='Organization Name'
                      name='name'
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                      className={'zitles-textfield'}/>
                  </Grid>
                  <Grid size={12}>
                    <FormControl required fullWidth error={touched.type && Boolean(errors.type)}>
                      <InputLabel>Organization Type</InputLabel>
                      <Select
                        id={'input-organization-type'}
                        name='type'
                        value={values.type}
                        label='Organization Type'
                        onBlur={handleBlur}
                        onChange={handleChange}>
                        {
                          getOrganizationTypeOptions().map((option: DropdownOption): JSX.Element => 
                            <MenuItem key={option.key} value={option.value}>{option.text}</MenuItem>
                          )
                        }
                      </Select>
                      <FormHelperText>{touched.type && errors.type}</FormHelperText>
                    </FormControl>
                  </Grid>
                    <Grid size={12}>
                      <Typography className={'form-section-title'}>Address Information</Typography>
                    </Grid>
                    <Grid size={12}>
                      <TextField
                        id={'input-address-line1'}
                        required
                        fullWidth
                        label='Address Line 1'
                        name='address.line1'
                        value={values.address?.line1 || ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.address?.line1 && Boolean(errors.address?.line1)}
                        helperText={touched.address?.line1 && errors.address?.line1}
                        className={'zitles-textfield'}/>
                    </Grid>
                    <Grid size={12}>
                      <FormControl required fullWidth error={touched.address?.state && Boolean(errors.address?.state)}>
                        <InputLabel>State</InputLabel>
                        <Select
                          id={'input-state'}
                          name='address.state'
                          value={values.address?.state || ''}
                          label='State'
                          onBlur={handleBlur}
                          onChange={handleChange}>
                          {
                            stateOptions.map((option: DropdownOption): JSX.Element => 
                              <MenuItem key={option.key} value={option.value}>{option.text}</MenuItem>
                            )
                          }
                        </Select>
                        <FormHelperText>{touched.address?.state && errors.address?.state}</FormHelperText>
                      </FormControl>
                    </Grid>
                    <Grid size={6}>
                      <TextField
                        id={'input-city'}
                        required
                        fullWidth
                        label='City'
                        name='address.city'
                        value={values.address?.city || ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.address?.city && Boolean(errors.address?.city)}
                        helperText={touched.address?.city && errors.address?.city}
                        className={'zitles-textfield'}/>
                    </Grid>
                    <Grid size={6}>
                      <TextField
                        id={'input-zip'}
                        required
                        fullWidth
                        label='ZIP Code'
                        name='address.zip'
                        value={values.address?.zip || ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.address?.zip && Boolean(errors.address?.zip)}
                        helperText={touched.address?.zip && errors.address?.zip}
                        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                        className={'zitles-textfield'}/>
                    </Grid>
                  <Grid size={12}>
                    <TextField
                      id={'input-phone-number'}
                      required
                      fullWidth
                      label='Phone Number'
                      name='phoneNumber'
                      value={values.phoneNumber}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.phoneNumber && Boolean(errors.phoneNumber)}
                      helperText={touched.phoneNumber && errors.phoneNumber}
                      inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                      className={'zitles-textfield'}/>
                  </Grid>
                </Grid>
                <Box className={'form-actions'}>
                  <Button
                    id={'btn-back-to-options'}
                    disableTouchRipple
                    onClick={handleBackToOptions}
                    className={'zitles-button cancel'}>
                    Back
                  </Button>
                  <Button
                    id={'btn-submit-organization'}
                    type='submit'
                    variant='contained'
                    disabled={submitLoading}
                    className={`zitles-button ${submitLoading ? 'disable' : ''}`}>
                    {
                      submitLoading ? 'Updating Profile' : 'Update Profile'
                    }
                  </Button>
                </Box>
              </Form>
            )}
          </Formik>
        </Paper>
      </Box>
    );
  }

  return (
    <Box className={'page-container'}>
      {
        loading
          ? <LoadingComponent/>
          : !userDetailsCompleted
            ? renderUserDetailsForm()
            : selectedOption === RegistrationOption.NONE
              ? renderOptionSelection()
              : selectedOption === RegistrationOption.EXISTING
                ? renderInvitationCodeForm()
                : currentStep === UserOnboardStep.ORGANIZATION_DETAILS && userOnboardDto
                  ? renderOrganizationForm()
                  : renderUserDetailsForm()
      }
    </Box>
  );
}

export default observer(SelfRegistration);
